import { useEffect, useState } from 'react';

/**
 * Hook to manage the expanded/collapsed state of a navigation item with children.
 * The item is expanded when it's active (in the current section) or manually expanded by the user.
 * Manual state is reset when navigating away from the section.
 *
 * @param isActive - Whether the navigation item is currently active (in the current section)
 * @returns An object containing the expanded state and a toggle function
 */
export function useNavItemExpanded(isActive: boolean) {
  const [manuallyExpanded, setManuallyExpanded] = useState(false);
  const [manuallyCollapsed, setManuallyCollapsed] = useState(false);

  // Reset manual states when navigating away from the section
  useEffect(() => {
    if (!isActive) {
      setManuallyExpanded(false);
      setManuallyCollapsed(false);
    }
  }, [isActive]);

  // Expanded when in section or user opened it; reset when navigating away
  const expanded = (isActive || manuallyExpanded) && !manuallyCollapsed;

  /**
   * Toggles the expanded state of the navigation item.
   * If currently expanded, it will collapse; if collapsed, it will expand.
   */
  const toggleExpanded = () => {
    if (expanded) {
      setManuallyCollapsed(true);
      setManuallyExpanded(false);
    } else {
      setManuallyCollapsed(false);
      setManuallyExpanded(true);
    }
  };

  return {
    expanded,
    toggleExpanded,
  };
}
